import {
  MaterialReactTable,
  MaterialReactTableProps,
  MRT_Localization,
  MRT_TableState
} from 'material-react-table';
import ButtonDefault from './ButtonDefault';
import ButtonDelete from './ButtonDelete';
import ButtonEdit from './ButtonEdit';
import ButtonStatus from './ButtonStatus';
import ButtonView from './ButtonView';
import { OnChangeFn, PaginationState } from '@tanstack/table-core';

interface ITableProps {
  columns: any[];
  data: any[];
  initialState?: Partial<MRT_TableState<any>>;
  state?: Partial<MRT_TableState<any>>;
  totalRows?: number;
  onPaginationChange?: OnChangeFn<PaginationState>;
}

const Table = ({ columns, data, initialState, state, totalRows, onPaginationChange }: ITableProps) => {
  const normalizedColumns = columns.map(col => ({
    ...col,
    header: col.header || col.Header || col.accessorKey || col.id || "Sem Nome"
  }));

  const MRT_Localization_PT: MRT_Localization = {
    actions: 'Ações',
    and: 'e',
    cancel: 'Cancelar',
    changeFilterMode: 'Alterar modo de filtro',
    changeSearchMode: 'Alterar modo de busca',
    clearFilter: 'Limpar filtro',
    clearSearch: 'Limpar busca',
    clearSelection: 'Limpar seleção',
    clearSort: 'Limpar ordenação',
    clickToCopy: 'Clique para copiar',
    copy: 'Copiar',
    collapse: 'Recolher',
    collapseAll: 'Recolher tudo',
    columnActions: 'Ações da coluna',
    copiedToClipboard: 'Copiado para a área de transferência',
    dropToGroupBy: 'Solte para agrupar por {column}',
    edit: 'Editar',
    expand: 'Expandir',
    expandAll: 'Expandir tudo',
    filterArrIncludes: 'Inclui',
    filterArrIncludesAll: 'Inclui todos',
    filterArrIncludesSome: 'Inclui',
    filterBetween: 'Entre',
    filterBetweenInclusive: 'Entre inclusive',
    filterByColumn: 'Filtrar por {column}',
    filterContains: 'Contém',
    filterEmpty: 'Vazio',
    filterEndsWith: 'Termina com',
    filterEquals: 'Igual a',
    filterEqualsString: 'Igual a',
    filterFuzzy: 'Difuso',
    filterGreaterThan: 'Maior que',
    filterGreaterThanOrEqualTo: 'Maior ou igual a',
    filterInNumberRange: 'Entre',
    filterIncludesString: 'Contém',
    filterIncludesStringSensitive: 'Contém',
    filterLessThan: 'Menor que',
    filterLessThanOrEqualTo: 'Menor ou igual a',
    filterMode: 'Modo de filtro: {filterType}',
    filterNotEmpty: 'Não vazio',
    filterNotEquals: 'Não igual a',
    filterStartsWith: 'Começa com',
    filterWeakEquals: 'Igual a',
    filteringByColumn: 'Filtrando por {column} - {filterType} {filterValue}',
    goToFirstPage: 'Ir para a primeira página',
    goToLastPage: 'Ir para a última página',
    goToNextPage: 'Ir para a próxima página',
    goToPreviousPage: 'Ir para a página anterior',
    grab: 'Pegar',
    groupByColumn: 'Agrupar por {column}',
    groupedBy: 'Agrupado por',
    hideAll: 'Ocultar tudo',
    hideColumn: 'Ocultar coluna {column}',
    max: 'Máximo',
    min: 'Mínimo',
    move: 'Mover',
    noRecordsToDisplay: 'Nenhum registro para exibir',
    noResultsFound: 'Nenhum resultado encontrado',
    of: 'de',
    or: 'ou',
    pin: 'Fixar',
    pinToLeft: 'Fixar à esquerda',
    pinToRight: 'Fixar à direita',
    resetColumnSize: 'Redefinir tamanho da coluna',
    resetOrder: 'Redefinir ordem',
    rowActions: 'Ações da linha',
    rowNumber: '#',
    rowNumbers: 'Números das linhas',
    rowsPerPage: 'Linhas por página',
    save: 'Salvar',
    search: 'Buscar',
    selectedCountOfRowCountRowsSelected:
      '{selectedCount} de {rowCount} linha(s) selecionada(s)',
    select: 'Selecionar',
    showAll: 'Mostrar todos',
    showAllColumns: 'Mostrar todas as colunas',
    showHideColumns: 'Mostrar/Ocultar colunas',
    showHideFilters: 'Mostrar/Ocultar filtros',
    showHideSearch: 'Mostrar/Ocultar busca',
    sortByColumnAsc: 'Ordenar por {column} crescente',
    sortByColumnDesc: 'Ordenar por {column} decrescente',
    sortedByColumnAsc: 'Ordenado por {column} crescente',
    sortedByColumnDesc: 'Ordenado por {column} decrescente',
    thenBy: ', depois por ',
    toggleDensity: 'Alternar densidade',
    toggleFullScreen: 'Alternar tela cheia',
    toggleSelectAll: 'Alternar seleção de tudo',
    toggleSelectRow: 'Alternar seleção da linha',
    toggleVisibility: 'Alternar visibilidade',
    ungroupByColumn: 'Desagrupar por {column}',
    unpin: 'Desfixar',
    unpinAll: 'Desfixar tudo',
  };

  const isManualPagination = totalRows !== undefined && typeof onPaginationChange === 'function';

  let effectiveState = state;
  if (!isManualPagination && state && (state as any).pagination) {
    const { pagination, ...rest } = state as any;
    effectiveState = rest;
  }

  const tableProps: MaterialReactTableProps<any> = {
    columns: normalizedColumns,
    data,
    localization: MRT_Localization_PT,
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 20, 25, 50, 100, 200]
    },
    paginationDisplayMode: 'pages',
    enableSorting: false,
    enableFilters: false,
    initialState,
    state: effectiveState,
    manualPagination: isManualPagination
  };

  if (isManualPagination) {
    tableProps.rowCount = totalRows;
    tableProps.onPaginationChange = onPaginationChange;
  }

  return <MaterialReactTable {...tableProps} />;
};

export { ButtonDefault, ButtonDelete, ButtonEdit, ButtonStatus, ButtonView };
export default Table;